package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatToFullString
import core.dateformat.utils.stringToLocalDate
import com.essntl.core.utils.viewmodel.State
import com.essntl.features.client.ui.ClientDialogButton
import com.essntl.features.profile.ui.OwnerItem
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.domain.model.ProposalModes
import com.essntl.features.proposal.domain.model.historyLink
import com.essntl.features.proposal.domain.model.shareLink
import com.essntl.features.proposal.ui.allproposal.AllProposalEvent
import com.essntl.features.proposal.ui.allproposal.AllProposalNavigation
import com.essntl.features.proposal.ui.allproposal.AllProposalState
import com.essntl.features.request.domain.model.RequestModel
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.primary.EsSmallPrimaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.tooltip.EsTooltip
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.EsRowDefaultItem
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.rememberEquals
import com.essntl.uikit.rememberFind
import com.essntl.uikit.rememberNumberOfDaysPrettyString
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.dateformat.ui.formatDate
import core.dateformat.ui.formatDateRange

@Composable
fun ProposalItem(
    state: AllProposalState,
    onEvent: (AllProposalEvent) -> Unit,
    onNavigation: (AllProposalNavigation) -> Unit,
    index: Int,
    proposal: ProposalModel,
    isSelected: Boolean,
    isUpdateProposalOwnerLoading: Boolean,
    modifier: Modifier = Modifier,
) {
    var isExpanded by rememberSaveable {
        mutableStateOf(false)
    }

    var isResetProposalExpanded = rememberSaveable {
        mutableStateOf(false)
    }

    var proposalToReset by remember {
        mutableStateOf<ProposalModel?>(null)
    }

    val areas =
        rememberContains(
            state.getAllArea.data.orEmpty(),
            proposal.areas.orEmpty().toSet(),
        ) { area -> area.id }

    val request =
        rememberFind(
            state.getAllRequest.data.orEmpty(),
            proposal.requestId,
        ) { request -> request.id }

    val openConfirmProposalDialog = rememberSaveable { mutableStateOf(false) }

    val interactionSource = remember { MutableInteractionSource() }

    val isHovered by interactionSource.collectIsHoveredAsState()

    val isPressed by interactionSource.collectIsPressedAsState()

    Column(
        modifier =
            modifier
                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier =
                Modifier
                    .background(
                        when {
                            isSelected ->
                                MaterialTheme.esColorScheme.tintedPressed

                            isPressed ->
                                MaterialTheme.esColorScheme.tintedPressed

                            isHovered ->
                                MaterialTheme.esColorScheme.tintedHover

                            index % 2 == 0 ->
                                MaterialTheme.esColorScheme.bgSecondaryHover.copy(alpha = 0.7f)

                            else ->
                                Color.Transparent
                        },
                    )
                    .padding(
                        vertical = EsConstants.ListRowVerticalPadding,
                        horizontal = EsConstants.ListRowHorizontalPadding,
                    )
                    .height(EsConstants.ListRowItemHeight)
                    .pointerHoverIcon(PointerIcon.Hand)
                    .focusProperties { canFocus = false }
                    .clickable(
                        interactionSource = interactionSource,
                        indication = null,
                        onClick = {
                            onNavigation(AllProposalNavigation.Proposal(proposal.id))
                        },
                    ),
        ) {
            // Proposal title
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier =
                    Modifier
                        .weight(2f)
                        .fillMaxHeight(),
            ) {
                Text(
                    text = proposal.title ?: "",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.s2SemiBold16,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.weight(1f))
            }

            // Owner
            ProposalRowItem(
                modifier =
                    Modifier
                        .width(80.dp)
                        .padding(end = 1.5f.spacing),
            ) {
                OwnerItem(
                    ownerId = proposal.owner?.id,
                    getAllProfile = state.getAllProfile,
                    isUpdateOwnerLoading = isUpdateProposalOwnerLoading,
                    enabled = true,
                    onOwnerChange = { profile ->
                        onEvent(
                            AllProposalEvent.UpdateProposalOwner(
                                proposal,
                                profile,
                            ),
                        )
                    },
                    modifier =
                        Modifier
                            .align(Alignment.CenterStart),
                )
            }

            // Primary Traveller
            if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
                EsRowDefaultItem(
                    text = "",
                    extraContent = {
                        ClientDialogButton(
                            client = proposal.primaryClient,
                            onClick = {
                                onNavigation(AllProposalNavigation.EditClient(proposal))
                            },
                        )
                    },
                )

            // Destination
            ProposalRowDefaultItem(
                text =
                    areas.map { it.name ?: "" }.filter { it.isNotEmpty() }.joinToString(", ")
                        .ifEmpty { "Unknown destination" },
            )

            // Dates
            val dateDiff = rememberNumberOfDaysPrettyString(
                proposal.fromDate,
                proposal.toDate,
            )

            ProposalRowDefaultItem(
                text =
                    listOf(
                        proposal.fromDate,
                        proposal.toDate,
                    ).formatDateRange() + '\n' + dateDiff,
                maxLines = 2,
            )

            // Status
            ProposalRowItem {
                proposal.status?.let {
                    EsTooltip(
                        text = "Changed ${stringToLocalDate(proposal.statusUpdatedAt)?.formatToFullString() ?: "-"}",
                    ) {
                        ProposalStatusDropDown(
                            status = it,
                            enabled = it != ProposalModel.Status.Confirmed,
                            onStatusSelected = { newStatus ->
                                if (newStatus != proposal.status) {
                                    if (newStatus == ProposalModel.Status.Confirmed) {
                                        openConfirmProposalDialog.value = true
                                    } else {
                                        onEvent(
                                            AllProposalEvent.UpdateProposalStatus(
                                                proposal,
                                                newStatus,
                                            ),
                                        )
                                    }
                                }
                            },
                        )
                    }
                }
            }

            // Travel Agent
            if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
                EsRowDefaultItem(
                    text = "",
                    extraContent = {
                        TravelAgentDialogButton(
                            travelAgent = proposal.travelAgent,
                            onClick = {
                                onNavigation(AllProposalNavigation.EditTravelAgent(proposal))
                            },
                        )
                    },
                )

            // PAX
            if (state.filterOptions.selectedProposalsMode == ProposalModes.Leads.index)
                ProposalRowDefaultItem(
                    text = proposal.guestCount.toString(),
                    modifier = Modifier.weight(0.5f),
                )

            if (state.filterOptions.selectedProposalsMode == ProposalModes.Designer.index) {
                ProposalRowItem(
                    modifier = Modifier.weight(0.5f),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = "v${proposal.version}",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Medium14,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )
                        Spacer(modifier = Modifier.width(1.spacing))

                        if (proposal.version > 1) {
                            EsTooltip(
                                text = "Show version history",
                            ) {
                                EsSmallOutlineIconButton(
                                    onClick = {
                                        if (!isExpanded) onEvent(AllProposalEvent.GetProposalHistoryList(proposal.id))
                                        isExpanded = !isExpanded
                                    },
                                    loading = state.updateProposalLoading.contains(proposal.id),
                                    iconRes = EsIcons.ArrowDown,
                                    modifier =
                                        Modifier
                                            .focusProperties { canFocus = false }
                                            .graphicsLayer {
                                                transformOrigin = TransformOrigin.Center
                                                rotationZ = if (isExpanded) 180f else 0f
                                            },
                                )
                            }
                        }
                    }
                }

                // Actions
                ProposalRowItem {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.5f.spacing),
                    ) {
                        val uriHandler = LocalUriHandler.current
                        val toolTipText = if (proposal.isPublished) "Link" else "Share link not created yet"
                        val linkButtonAlpha = if (proposal.isPublished) 1f else 0.6f

                        EsTooltip(
                            text = toolTipText,
                        ) {
                            EsSmallOutlineIconButton(
                                onClick = {
                                    uriHandler.openUri(
                                        uri = proposal.shareLink,
                                    )
                                },

                                iconRes = EsIcons.Browse,
                                enabled = proposal.isPublished,
                                modifier =
                                    Modifier
                                        .alpha(linkButtonAlpha)
                                        .focusProperties { canFocus = false },
                            )
                        }

                        EsTooltip(
                            text = "Edit",
                        ) {
                            EsSmallPrimaryIconButton(
                                onClick = {
                                    onNavigation(AllProposalNavigation.EditProposal(proposal.id))
                                },
                                iconRes = EsIcons.Edit,
                                modifier = Modifier.focusProperties { canFocus = false },
                            )
                        }
                    }
                }
            }

            // Created
            ProposalRowDefaultItem(
                text = proposal.createdAt.formatDate("."),
                modifier = Modifier.weight(0.7f),
            )

            // Modified
            ProposalRowDefaultItem(
                text = proposal.updatedAt?.formatDate(".") ?: proposal.createdAt.formatDate("."),
                modifier = Modifier.weight(0.7f),
            )

            // Request Date
            ProposalRowDefaultItem(
                text = proposal.requestReceivedDate?.formatDate(".") ?: proposal.createdAt.formatDate("."),
                modifier = Modifier.weight(0.7f),
            )
        }

        AnimatedVisibility(isExpanded) {
            ProposalHistoryColumn(
                state = state,
                proposal = proposal,
                request = request,
                onOpenProposalHistory = {
                    onNavigation(AllProposalNavigation.ProposalHistory(it.id, proposal.status))
                },
                onCopyProposalHistory = {
                    isResetProposalExpanded.value = true
                    proposalToReset = it

                },
            )
        }

        ConfirmProposalPopup(
            isOpen = openConfirmProposalDialog,
            onDismissRequest = {
                openConfirmProposalDialog.value = false
            },
            onEditProposalStatus = {
                onEvent(
                    AllProposalEvent.UpdateProposalStatus(
                        proposal,
                        ProposalModel.Status.Confirmed,
                    ),
                )
            },
        )

        ResetProposalPopup(
            isOpen = isResetProposalExpanded,
            onDismissRequest = {
                isResetProposalExpanded.value = false
                proposalToReset = null
            },
            onCopyProposalHistory = {
                proposalToReset?.let { p ->
                    onEvent(AllProposalEvent.MakeProposalCopy(p))
                }
            },
        )
    }
}

@Composable
private fun RowScope.ProposalRowDefaultItem(
    text: String?,
    maxLines: Int = 1,
    modifier: Modifier = Modifier.weight(1f),
) {
    ProposalRowItem(
        modifier = modifier,
    ) {
        Text(
            text = text ?: "",
            color = MaterialTheme.esColorScheme.textPrimary,
            style = MaterialTheme.esTypography.p2Medium14,
            maxLines = maxLines,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
internal fun RowScope.ProposalRowItem(
    modifier: Modifier = Modifier.weight(1f),
    isFirst: Boolean = false,
    content: @Composable BoxScope.() -> Unit = {},
) {
    if (!isFirst) {
        Spacer(
            modifier =
                Modifier
                    .padding(end = 1.5f.spacing)
                    .width(1.dp)
                    .fillMaxHeight()
                    .background(Color.Transparent),
        )
    }

    Box(
        modifier = modifier,
    ) {
        content()
    }
}

@Composable
private fun ProposalHistoryColumn(
    state: AllProposalState,
    proposal: ProposalModel,
    request: RequestModel?,
    onOpenProposalHistory: (proposal: ProposalModel) -> Unit,
    onCopyProposalHistory: (proposal: ProposalModel) -> Unit,
) {
    val getAllProposalHistory = state.getAllProposalHistoryMap[proposal.id] ?: State()

    if (getAllProposalHistory.isLoading) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.fillMaxWidth().height(60.dp),
        ) {
            EsSpinner()
        }
    } else if (getAllProposalHistory.isFailure) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.fillMaxWidth().height(60.dp),
        ) {
            Text(
                text = "Something goes wrong!",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )
        }
    } else if (getAllProposalHistory.data.orEmpty().isEmpty()) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier.fillMaxWidth().height(60.dp),
        ) {
            Text(
                text = "No proposal history found!",
                color = MaterialTheme.esColorScheme.textSecondary,
                style = MaterialTheme.esTypography.p1Medium16,
            )
        }
    } else {
        Column(
            verticalArrangement = Arrangement.spacedBy(1.spacing),
        ) {
            getAllProposalHistory.data
                .orEmpty()
                .forEach { proposalHistory ->
                    val areas = rememberContains(
                        state.getAllArea.data.orEmpty(),
                        proposalHistory.areas
                            .orEmpty()
                            .toSet(),
                    ) { area -> area.id }

                    val isSelected = rememberEquals(proposalHistory.id, setOf(state.selectedProposalId)) { it }

                    key(proposalHistory.id) {
                        ProposalHistoryItem(
                            parentProposal = proposal,
                            proposalHistory = proposalHistory,
                            request = request,
                            isSelected = isSelected,
                            areas = areas,
                            onOpenProposalHistory = {
                                onOpenProposalHistory(proposalHistory)
                            },
                            onCopyProposalHistory = {
                                onCopyProposalHistory(proposalHistory)
                            },
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
        }
    }
}